<template>
  <div id="projects-docu-managers">
    <div id="docu-managers-top-panel">
      <div id="title-container">
        <h2>{{ t('projectsDocuManagers.docuManager', 2) }}</h2>
        <div id="docu-managers-counter">{{ docuManagers?.length ?? 0 }}</div>
      </div>
      <vf-button
        v-if="isUserRole(addingDocuManagerAllowedRoles)"
        :size="buttonSizes.large"
        :variant="buttonVariants.secondary"
        @click="openAddDocuManagerModal"
      >
        {{ t('projectsDocuManagers.addDocuManager') }}
      </vf-button>
    </div>

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      action-icon="delete"
      :display-action-icon="isUserRole(addingDocuManagerAllowedRoles)"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="docuManagers"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
      @action-icon-click="deleteDocuManagerFromProject"
    ></table-wrapper>

    <ad-group-modal
      ref="modalRef"
      modal-id="docu-managers-modal"
      :group="adGroup"
      :title="t('projectsDocuManagers.addDocuManager')"
      @selected-user="handleAddDocuManager"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '../../composables/useToast';
  import axios, { AxiosError } from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import { AdGroup, IDocuManager, Role } from 'types';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import AdGroupModal from '../AdGroupModal/AdGroupModal.vue';

  const { t } = useI18n();
  const { createToast, createErrorToast, createWarningToast } = useToast();

  const props = defineProps<{ projectId: string }>();

  const docuManagers = ref<IDocuManager[]>([]);
  const { pageOptions, filters, isLoading } = useTableWrapper<IDocuManager>(
    TableMode.CLIENT_SIDE,
    'lastName',
    getDocuManagers,
    undefined,
    false,
  );

  const filterConfiguration = new TableFiltersBuilder<IDocuManager>({
    translationKey: 'projectsDocuManagers',
  })
    .addInputFilter('firstName', { width: '208px' })
    .addInputFilter('lastName', { width: '208px' })
    .addInputFilter('email', { width: '208px' })
    .build();

  const tableFields = computed(() =>
    new TableFieldsBuilder<IDocuManager>(
      {
        sortable: true,
        labelTranslationKey: 'generic',
      },
      { actionIcon: true },
    )
      .addField('firstName', { width: '36%' })
      .addField('lastName', { width: '28%' })
      .addField('email', { width: '36%' })
      .build(),
  );

  const adGroup = AdGroup.DOCU_MANAGER;
  const addingDocuManagerAllowedRoles = [Role.Admin, Role.DocuManager];

  async function getDocuManagers() {
    try {
      const response = await axios.get(
        endpointsList.projects.getDocuManagersForProject(props.projectId),
      );
      docuManagers.value = response.data;
      const isUnkownUser = docuManagers.value.some(
        (docuManager) => docuManager.firstName === 'unknown',
      );
      if (isUnkownUser) {
        createWarningToast(t('projectsDocuManagers.warnings.unknownUser'));
      }
    } catch {
      createErrorToast(
        t('projectsDocuManagers.errors.couldNotGetDocuManagers'),
      );
    }
  }

  async function deleteDocuManagerFromProject(docuManager: IDocuManager) {
    try {
      await axios.delete(
        endpointsList.projects.removeDocuManagerFromProject(
          props.projectId,
          docuManager.oid,
        ),
      );
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response?.status === 409) {
          createErrorToast(t('projectsDocuManagers.errors.cannotRemoveLast'));
        } else if (err.response?.status === 403) {
          createErrorToast(t('projectsDocuManagers.errors.cannotRemoveSelf'));
        } else {
          createErrorToast(
            t('projectsDocuManagers.errors.errorRemovingDocuManager'),
          );
        }
      } else {
        createErrorToast(
          t('projectsDocuManagers.errors.errorRemovingDocuManager'),
        );
      }
    } finally {
      await getDocuManagers();
    }
  }

  async function handleAddDocuManager(docuManager: IDocuManager) {
    try {
      const response = await axios.patch(
        endpointsList.projects.addDocuManagerToProject(
          props.projectId,
          docuManager.oid,
        ),
      );

      if (response.status === 200) {
        getDocuManagers();
        createToast(
          t('addDocuManagerToProject.docuManagerAdded'),
          t('addDocuManagerToProject.docuManagerAdded'),
          'success',
        );
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.response?.status === 409) {
        createErrorToast(
          t('projectsDocuManagers.errors.docuManagerAlreadyAdded'),
        );
        return;
      }

      createErrorToast(t('errors.error'));
    }
  }

  const modalRef = ref<InstanceType<typeof AdGroupModal> | null>(null);
  function openAddDocuManagerModal() {
    modalRef.value?.show();
  }
</script>

<style scoped lang="scss">
  #projects-docu-managers {
    border-top: 1px solid $border-color;
    padding-top: 31px;

    #docu-managers-top-panel {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 27px;

      #title-container {
        justify-self: flex-start;
        display: flex;
        align-items: flex-start;
        column-gap: 10px;

        h2 {
          font-size: 36px;
          font-weight: 700;
        }

        #docu-managers-counter {
          background-color: black;
          color: white;
          font-size: 12px;
          border-radius: 50px;
          padding: 3px 20px;
        }
      }
    }

    @include table-action-icon-on-hover;
  }
</style>
